import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Edit2,
  Filter,
  MoreVertical,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CompactSelect, type SelectOption } from "../shared/CompactSelect";
import { EmptyState } from "../shared/EmptyState";
import { FilterPanel } from "../shared/FilterPanel";
import { formatCurrency, formatPercent } from "../shared/currency";
import {
  DEFAULT_TRIP_FILTERS,
  QUICK_TRIP_FILTERS,
  type QuickTripFilter,
  type SortableTripField,
  type TripFilters,
  type TripSortState,
} from "./selectors";
import { calculateFinancials } from "./rules";
import {
  BOOKING_TYPES,
  TRIP_STATUSES,
  VEHICLE_SOURCES,
  type Trip,
  type TripStatus,
} from "./types";

type TripRegisterPageProps = {
  trips: Trip[];
  totalTrips: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: TripFilters;
  onFiltersChange: (filters: TripFilters) => void;
  onEdit: (trip: Trip) => void;
  onRepeat: (trip: Trip) => void;
  onDelete: (tripId: string) => void;
  onStatusChange: (tripId: string, status: TripStatus) => void;
  onNewTrip: () => void;
};

const DEFAULT_SORT: TripSortState = { field: "date", direction: "desc" };

export function TripRegisterPage({
  trips,
  totalTrips,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  onEdit,
  onRepeat,
  onDelete,
  onStatusChange,
  onNewTrip,
}: TripRegisterPageProps) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<TripSortState>(DEFAULT_SORT);
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<TripFilters>(filters);
  const [draftSort, setDraftSort] = useState<TripSortState>(sort);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const pageSize = 10;
  const sortedTrips = useMemo(() => sortTrips(trips, sort), [trips, sort]);
  const pageCount = Math.max(1, Math.ceil(sortedTrips.length / pageSize));
  const paginatedTrips = useMemo(
    () => sortedTrips.slice((page - 1) * pageSize, page * pageSize),
    [page, sortedTrips]
  );
  const activeFilterCount = countTripFilters(filters, sort);

  useEffect(() => {
    setPage(1);
  }, [filters, searchQuery, sort, trips.length]);

  const openFilters = () => {
    setDraftFilters(filters);
    setDraftSort(sort);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    onFiltersChange(draftFilters);
    setSort(draftSort);
    setIsFilterOpen(false);
  };

  const toggleSort = (field: SortableTripField) => {
    setSort((current) => ({
      field,
      direction:
        current.field === field && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  return (
    <section className="orbit-card overflow-hidden">
      <div className="border-b border-slate-200 p-3 sm:p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-950">Trip Register</h2>
            <p className="text-xs font-medium text-slate-500 sm:text-sm">
              {trips.length} of {totalTrips} trips
            </p>
          </div>
          <div className="hidden text-right text-xs text-slate-500 lg:block">
            Ten trips per page
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search trip, party, vehicle, route"
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-300 py-2 pl-10 pr-3 text-sm outline-none"
            />
          </div>
          <button
            ref={filterButtonRef}
            type="button"
            onClick={openFilters}
            aria-label="Trip filters"
            aria-expanded={isFilterOpen}
            className="orbit-tab relative inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-bold"
          >
            <Filter size={17} />
            <span className="hidden sm:inline">Filter</span>
            {activeFilterCount > 0 ? (
              <span className="orbit-tone-amber min-w-5 rounded-full px-1.5 py-0.5 text-center text-[10px] font-bold">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        </div>
        {activeFilterCount > 0 ? (
          <ActiveTripFilters
            filters={filters}
            sort={sort}
            onFiltersChange={onFiltersChange}
            onSortChange={setSort}
          />
        ) : null}
      </div>

      {paginatedTrips.length === 0 ? (
        <div className="p-4">
          <EmptyState
            title="No trips match this view"
            body="Clear filters or create a new trip."
            action={
              <button
                type="button"
                onClick={onNewTrip}
                className="orbit-button-primary min-h-11 px-4 py-2 text-sm"
              >
                New Trip
              </button>
            }
          />
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100 lg:hidden">
            {paginatedTrips.map((trip) => {
              const financials = calculateFinancials(trip);
              return (
                <article key={trip.id} className="px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-sm font-semibold text-slate-950">
                          {trip.tripNo}
                        </p>
                        <span className="shrink-0 text-[11px] text-slate-500">
                          {formatShortDate(trip.date)}
                        </span>
                      </div>
                    </div>
                    <TripActionMenu
                      trip={trip}
                      isOpen={openActionId === trip.id}
                      onToggle={() =>
                        setOpenActionId((current) => (current === trip.id ? null : trip.id))
                      }
                      onClose={() => setOpenActionId(null)}
                      onEdit={onEdit}
                      onRepeat={onRepeat}
                      onDelete={onDelete}
                    />
                  </div>
                  <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
                    <p className="min-w-0 truncate text-sm font-medium text-slate-700">
                      {trip.partyName || "Party not set"}
                    </p>
                    <StatusSelect
                      status={trip.status}
                      onChange={(status) => onStatusChange(trip.id, status)}
                      compact
                    />
                  </div>
                  <p className="mt-1.5 truncate text-xs text-slate-500">
                    <span className="font-semibold text-slate-700">
                      {trip.vehicleNo || "Vehicle not set"}
                    </span>
                    {` · ${trip.origin || "Origin"} - ${trip.destination || "Destination"}`}
                  </p>
                  <div className="mt-1.5 grid grid-cols-2 gap-2 text-sm">
                    <p className="font-semibold text-slate-950">
                      <span className="text-[11px] font-medium text-slate-500">Profit </span>
                      {formatCurrency(financials.operatingProfit)}
                    </p>
                    <p className="text-right font-semibold text-amber-700">
                      <span className="text-[11px] font-medium text-slate-500">Due </span>
                      {formatCurrency(financials.outstanding)}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="hidden lg:block">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-[16%] px-3 py-2.5 font-semibold">
                    <SortButton label="Trip" field="date" sort={sort} onSort={toggleSort} />
                  </th>
                  <th className="w-[24%] px-3 py-2.5 font-semibold">Party / Route</th>
                  <th className="w-[18%] px-3 py-2.5 font-semibold">Vehicle / Booking</th>
                  <th className="w-[12%] px-3 py-2.5 text-right font-semibold">
                    <SortButton label="Profit" field="profit" sort={sort} onSort={toggleSort} align="right" />
                  </th>
                  <th className="w-[12%] px-3 py-2.5 text-right font-semibold">
                    <SortButton label="Due" field="outstanding" sort={sort} onSort={toggleSort} align="right" />
                  </th>
                  <th className="w-[14%] px-3 py-2.5 font-semibold">Status</th>
                  <th className="w-[4%] px-2 py-2.5 text-right font-semibold">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedTrips.map((trip) => {
                  const financials = calculateFinancials(trip);
                  return (
                    <tr key={trip.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <p className="truncate font-semibold text-slate-950">{trip.tripNo}</p>
                        <p className="text-xs text-slate-500">{trip.date}</p>
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="truncate font-semibold text-slate-950">
                          {trip.partyName || "Party not set"}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          {trip.origin || "Origin"} - {trip.destination || "Destination"}
                        </p>
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="truncate font-semibold text-slate-950">
                          {trip.vehicleNo || "Vehicle not set"}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          {trip.bookingType} · {trip.vehicleSource}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <p className="font-bold text-slate-950">
                          {formatCurrency(financials.operatingProfit)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatPercent(financials.marginPct)}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-amber-700">
                        {formatCurrency(financials.outstanding)}
                      </td>
                      <td className="px-3 py-2.5">
                        <StatusSelect
                          status={trip.status}
                          onChange={(status) => onStatusChange(trip.id, status)}
                        />
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <TripActionMenu
                          trip={trip}
                          isOpen={openActionId === trip.id}
                          onToggle={() =>
                            setOpenActionId((current) =>
                              current === trip.id ? null : trip.id
                            )
                          }
                          onClose={() => setOpenActionId(null)}
                          onEdit={onEdit}
                          onRepeat={onRepeat}
                          onDelete={onDelete}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {trips.length > 0 ? (
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-3 py-2.5 text-sm sm:px-4">
          <p className="font-medium text-slate-500">Page {page} of {pageCount}</p>
          <div className="flex items-center gap-2">
            <PagerButton
              label="Previous"
              icon={<ChevronLeft size={16} />}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page === 1}
            />
            <PagerButton
              label="Next"
              icon={<ChevronRight size={16} />}
              iconAfter
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page === pageCount}
            />
          </div>
        </div>
      ) : null}

      <FilterPanel
        open={isFilterOpen}
        title="Trip filters"
        triggerRef={filterButtonRef}
        onClose={() => setIsFilterOpen(false)}
        onReset={() => {
          setDraftFilters(DEFAULT_TRIP_FILTERS);
          setDraftSort(DEFAULT_SORT);
        }}
        onApply={applyFilters}
        applyLabel="Show trips"
      >
        <TripFilterFields
          filters={draftFilters}
          sort={draftSort}
          onFiltersChange={setDraftFilters}
          onSortChange={setDraftSort}
        />
      </FilterPanel>
    </section>
  );
}

function TripFilterFields({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
}: {
  filters: TripFilters;
  sort: TripSortState;
  onFiltersChange: (filters: TripFilters) => void;
  onSortChange: (sort: TripSortState) => void;
}) {
  const setFilter = <K extends keyof TripFilters>(key: K, value: TripFilters[K]) =>
    onFiltersChange({ ...filters, [key]: value });

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Quick view</p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {QUICK_TRIP_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setFilter("quickFilter", filter)}
              className={`min-h-11 rounded-xl border px-3 py-2 text-left text-sm font-semibold ${
                filters.quickFilter === filter ? "orbit-tab-active" : "orbit-tab"
              }`}
            >
              {quickFilterLabel[filter]}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FilterField label="Status">
          <CompactSelect
            ariaLabel="Filter by status"
            value={filters.status}
            options={statusFilterOptions}
            onChange={(value) => setFilter("status", value)}
          />
        </FilterField>
        <FilterField label="Booking">
          <CompactSelect
            ariaLabel="Filter by booking type"
            value={filters.bookingType}
            options={bookingFilterOptions}
            onChange={(value) => setFilter("bookingType", value)}
          />
        </FilterField>
        <FilterField label="Vehicle source">
          <CompactSelect
            ariaLabel="Filter by vehicle source"
            value={filters.vehicleSource}
            options={vehicleSourceFilterOptions}
            onChange={(value) => setFilter("vehicleSource", value)}
          />
        </FilterField>
        <FilterField label="Sort by">
          <CompactSelect
            ariaLabel="Sort trips by"
            value={sort.field}
            options={tripSortOptions}
            onChange={(field) => onSortChange({ ...sort, field })}
          />
        </FilterField>
        <FilterField label="From">
          <input
            aria-label="Trips from date"
            type="date"
            value={filters.dateFrom}
            onChange={(event) => setFilter("dateFrom", event.target.value)}
            className="min-h-11 w-full rounded-xl border px-3 py-2 text-sm outline-none"
          />
        </FilterField>
        <FilterField label="To">
          <input
            aria-label="Trips to date"
            type="date"
            value={filters.dateTo}
            onChange={(event) => setFilter("dateTo", event.target.value)}
            className="min-h-11 w-full rounded-xl border px-3 py-2 text-sm outline-none"
          />
        </FilterField>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Order</p>
        <div className="orbit-raised mt-2 grid grid-cols-2 gap-1 p-1">
          {(["desc", "asc"] as TripSortState["direction"][]).map((direction) => (
            <button
              key={direction}
              type="button"
              onClick={() => onSortChange({ ...sort, direction })}
              className={`min-h-10 rounded-lg text-sm font-semibold ${
                sort.direction === direction ? "orbit-tab-active" : "orbit-tab"
              }`}
            >
              {direction === "desc" ? "Newest / Highest" : "Oldest / Lowest"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActiveTripFilters({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
}: {
  filters: TripFilters;
  sort: TripSortState;
  onFiltersChange: (filters: TripFilters) => void;
  onSortChange: (sort: TripSortState) => void;
}) {
  const chips: Array<{ key: string; label: string; clear: () => void }> = [];
  if (filters.quickFilter !== "all") {
    chips.push({
      key: "quick",
      label: quickFilterLabel[filters.quickFilter],
      clear: () => onFiltersChange({ ...filters, quickFilter: "all" }),
    });
  }
  if (filters.status !== "all") {
    chips.push({
      key: "status",
      label: filters.status,
      clear: () => onFiltersChange({ ...filters, status: "all" }),
    });
  }
  if (filters.bookingType !== "all") {
    chips.push({
      key: "booking",
      label: filters.bookingType,
      clear: () => onFiltersChange({ ...filters, bookingType: "all" }),
    });
  }
  if (filters.vehicleSource !== "all") {
    chips.push({
      key: "source",
      label: filters.vehicleSource,
      clear: () => onFiltersChange({ ...filters, vehicleSource: "all" }),
    });
  }
  if (filters.dateFrom || filters.dateTo) {
    chips.push({
      key: "date",
      label: `${filters.dateFrom || "Start"} to ${filters.dateTo || "Now"}`,
      clear: () => onFiltersChange({ ...filters, dateFrom: "", dateTo: "" }),
    });
  }
  if (sort.field !== DEFAULT_SORT.field || sort.direction !== DEFAULT_SORT.direction) {
    chips.push({
      key: "sort",
      label: `${tripSortLabel[sort.field]} ${sort.direction === "desc" ? "↓" : "↑"}`,
      clear: () => onSortChange(DEFAULT_SORT),
    });
  }

  return (
    <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.clear}
          className="orbit-tone-blue inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
        >
          {chip.label}
          <X size={13} />
        </button>
      ))}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block min-w-0">
      <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      {children}
    </div>
  );
}

function StatusSelect({
  status,
  onChange,
  compact = false,
}: {
  status: TripStatus;
  onChange: (status: TripStatus) => void;
  compact?: boolean;
}) {
  return (
    <CompactSelect
      ariaLabel="Trip status"
      value={status}
      options={statusOptions}
      onChange={onChange}
      className={compact ? "w-[122px] max-w-[44%] shrink-0" : "w-full min-w-[130px]"}
      buttonClassName={`rounded-full px-2.5 text-xs font-bold ${statusTone[status]}`}
    />
  );
}

function TripActionMenu({
  trip,
  isOpen,
  onToggle,
  onClose,
  onEdit,
  onRepeat,
  onDelete,
}: {
  trip: Trip;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit: (trip: Trip) => void;
  onRepeat: (trip: Trip) => void;
  onDelete: (tripId: string) => void;
}) {
  const run = (action: () => void) => {
    onClose();
    action();
  };
  return (
    <div className="relative inline-flex shrink-0 justify-end">
      <button
        type="button"
        onClick={onToggle}
        aria-label={`Trip actions for ${trip.tripNo}`}
        aria-expanded={isOpen}
        className="orbit-tab grid h-11 w-11 place-items-center rounded-xl"
      >
        <MoreVertical size={19} />
      </button>
      {isOpen ? (
        <div className="orbit-card absolute right-0 top-full z-30 mt-1 w-44 p-1.5 text-left shadow-2xl">
          <ActionButton icon={<Edit2 size={16} />} label="Edit Trip" onClick={() => run(() => onEdit(trip))} />
          <ActionButton icon={<Copy size={16} />} label="Repeat Trip" onClick={() => run(() => onRepeat(trip))} />
          <ActionButton
            icon={<Trash2 size={16} />}
            label="Delete Trip"
            onClick={() => run(() => onDelete(trip.id))}
            danger
          />
        </div>
      ) : null}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-11 w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50 ${
        danger ? "text-rose-700" : "text-slate-700"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function SortButton({
  label,
  field,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  field: SortableTripField;
  sort: TripSortState;
  onSort: (field: SortableTripField) => void;
  align?: "left" | "right";
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex w-full items-center gap-1 ${align === "right" ? "justify-end" : ""}`}
    >
      {label}
      <ArrowUpDown size={13} className={sort.field === field ? "text-slate-950" : "text-slate-400"} />
    </button>
  );
}

function PagerButton({
  label,
  icon,
  iconAfter = false,
  onClick,
  disabled,
}: {
  label: string;
  icon: React.ReactNode;
  iconAfter?: boolean;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-10 items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 font-semibold text-slate-700 disabled:opacity-45"
    >
      {!iconAfter ? icon : null}
      <span className="hidden sm:inline">{label}</span>
      {iconAfter ? icon : null}
    </button>
  );
}

function sortTrips(trips: Trip[], sort: TripSortState): Trip[] {
  return [...trips].sort((a, b) => {
    const aFinancials = calculateFinancials(a);
    const bFinancials = calculateFinancials(b);
    const values: Record<SortableTripField, [string | number, string | number]> = {
      date: [a.date, b.date],
      profit: [aFinancials.operatingProfit, bFinancials.operatingProfit],
      outstanding: [aFinancials.outstanding, bFinancials.outstanding],
    };
    const [left, right] = values[sort.field];
    const comparison =
      typeof left === "number" && typeof right === "number"
        ? left - right
        : String(left).localeCompare(String(right));
    return sort.direction === "asc" ? comparison : -comparison;
  });
}

const countTripFilters = (filters: TripFilters, sort: TripSortState): number =>
  Number(filters.quickFilter !== "all") +
  Number(filters.status !== "all") +
  Number(filters.bookingType !== "all") +
  Number(filters.vehicleSource !== "all") +
  Number(Boolean(filters.dateFrom || filters.dateTo)) +
  Number(sort.field !== DEFAULT_SORT.field || sort.direction !== DEFAULT_SORT.direction);

const formatShortDate = (value: string): string => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const statusTone: Record<TripStatus, string> = {
  Draft: "status-select-slate",
  Planned: "status-select-amber",
  Dispatched: "status-select-blue",
  "In Transit": "status-select-blue",
  Delivered: "status-select-emerald",
  Billed: "status-select-emerald",
  "Partially Settled": "status-select-amber",
  Settled: "status-select-emerald",
  Cancelled: "status-select-rose",
};

const quickFilterLabel: Record<QuickTripFilter, string> = {
  all: "All",
  active: "Active",
  pendingPayment: "Pending Payment",
  deliveredNotBilled: "Delivered Not Billed",
  lowMargin: "Low Margin",
  brokerTrips: "Broker Trips",
  ownVehicles: "Own Vehicles",
  marketVehicles: "Market Vehicles",
};

const statusOptions: SelectOption<TripStatus>[] = TRIP_STATUSES.map((status) => ({
  value: status,
  label: status,
}));
const statusFilterOptions: SelectOption<TripFilters["status"]>[] = [
  { value: "all", label: "All statuses" },
  ...statusOptions,
];
const bookingFilterOptions: SelectOption<TripFilters["bookingType"]>[] = [
  { value: "all", label: "All booking types" },
  ...BOOKING_TYPES.map((value) => ({ value, label: value })),
];
const vehicleSourceFilterOptions: SelectOption<TripFilters["vehicleSource"]>[] = [
  { value: "all", label: "All vehicle sources" },
  ...VEHICLE_SOURCES.map((value) => ({ value, label: value })),
];
const tripSortLabel: Record<SortableTripField, string> = {
  date: "Date",
  profit: "Profit",
  outstanding: "Outstanding",
};
const tripSortOptions: SelectOption<SortableTripField>[] = Object.entries(
  tripSortLabel
).map(([value, label]) => ({ value: value as SortableTripField, label }));
